import { mat2d, vec2, glMatrix } from "gl-matrix";

glMatrix.setMatrixArrayType(Array);

/**
 * Limits angles to 360 degrees. Shifts negative angles to positive by starting from 360 and counting backward.
 * @param {number} angle - The angle to limit in degrees.
 * @returns {number} The limited angle in degrees.
 */
export const limitAngle = (angle) => Math.abs(angle < 0 ? 360 + (angle % 360) : angle % 360);

/**
 * Takes in a point represented by a mat2d matrix, and rotates it around a provided center point by the provided amount of radians. Will modify the provided matrix. Negative radians rotate clockwise, and positive radians rotate counter clockwise.
 * @param {mat2d} matrix - The matrix to be rotated.
 * @param {number} pointX - The X coordinate of the point to rotate around.
 * @param {number} pointY - The Y coordinate of the point to rotate around.
 * @param {number} rads - The angle of rotation in radians.
 */
export const rotateMatrixAroundPoint = (matrix, pointX, pointY, rads) => {
  mat2d.translate(matrix, matrix, vec2.fromValues(pointX, pointY));
  mat2d.rotate(matrix, matrix, rads);
  mat2d.translate(matrix, matrix, vec2.fromValues(-pointX, -pointY));
};

/**
 * Will limit radians by a provided angle in degrees. For example: if provided constraint is 15 degrees, the radians will be rounded to the nearest 15 degree increment.
 * @param {number} rads - The angle in radians to constrain.
 * @param {number} constraintAngle - The angle in degrees to constrain radians by.
 * @returns {number} The constrained angle in radians.
 */
export const constrainRadiansToAngle = (rads, constraintAngle) => {
  const tickSize = getRadiansFromAngle(constraintAngle);
  const ticks = Math.round(getAngleFromRadians(rads) / constraintAngle);
  return ticks * tickSize;
};

/**
 * Converts degrees to it's absolute value in radians.
 * @param {number} [angle] - The angle in degrees to be converted. Nullish is treated as 0, since never-rotated elements store no angle.
 * @returns {number} The radian equivalent of the provided angle without positive or negative direction.
 */
export const getRadiansFromAngle = (angle) => (angle ?? 0) * (Math.PI / 180);

/**
 * Converts radians to degrees. Result is rounded to the closest whole number.
 * @param {number} rads - The angle in radians to be converted.
 * @returns {number} The degrees equivalent of the provided radians, rounded to the closest whole number.
 */
export const getAngleFromRadians = (rads) => Math.round(rads * (180 / Math.PI));
